<template>
  <div class="itbv">
    <ChatterDrawer
      :drawer="chatterDrawerOpen"
      type="Checkpoint"
      :typeValue="chatterDrawerTitle"
      :listData="chatterListData"
      object-type="checkpoint_evaluation"
      :object-id="recordId"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalChatter"
      iconName="org_icon_responsibility"
      @closeDrawer="onChatterDrawerClose"
      @requestMoreCheckpointsChatter="loadMoreChatter"
    />
    <!-- Filters bar: the design-system "Standard Filters Bar" (SearchBar) drives the
         Filters button (emits openFilters so the parent can open the
         InspectionsTableByVersionFilterModal — ER-11310), the removable filter chips
         and the Search employee input. A Reset All button sits beside it. ER-11345
         re-scope per Eric Kish's review: compose the standard component (the same one
         the sibling EditCheckpointsTable and every other table screen uses) instead of
         hand-rolling the bar. -->
    <div class="itbv__filters-bar">
      <SearchBar
        class="itbv__search-bar"
        data-testid="itbv-search-bar"
        label="Filters"
        :length="activeFilterChips.length"
        :chip-text="chipLabels"
        ghost-text="Search employee"
        input-custom-height="44"
        :value-search="employeeSearch"
        :filter-btn="true"
        :filter-btn-fit-content="true"
        :remove-column-class="true"
        @onClick="emitOpenFilters"
        @onSearch="onSearchText"
        @removeFilter="removeFilterByLabel"
      />
      <button
        v-if="canBulkReset"
        type="button"
        class="itbv__reset-all-button"
        data-testid="itbv-reset-all"
        :disabled="resetAllBusy || loading"
        @click="emitResetAll"
      >
        Reset All
      </button>
    </div>

    <div class="itbv__body" :aria-busy="loading ? 'true' : 'false'">
      <div
        v-if="loading"
        class="itbv__loader"
        role="status"
        aria-live="polite"
        aria-label="Loading inspections"
        data-testid="itbv-loader"
      >
        <span class="itbv__loader-spinner" aria-hidden="true" />
      </div>

    <div v-if="hasAnyRows" class="itbv__table-wrap">
      <table class="itbv__table">
        <!-- Fluid columns: Figma mins (Employee 200, Inspected By 200) grow to fill the
             panel. Badge columns are sized so "Inspection Passed" / "Not Applicable"
             chips are not clipped. Reset / Chatter stay icon-width. -->
        <colgroup>
          <col class="itbv__col itbv__col--employee" />
          <col class="itbv__col itbv__col--status" />
          <col class="itbv__col itbv__col--learning-seen" />
          <col class="itbv__col itbv__col--quiz" />
          <col class="itbv__col itbv__col--inspected-by" />
          <col class="itbv__col itbv__col--inspection" />
          <col class="itbv__col itbv__col--reset" />
          <col class="itbv__col itbv__col--chatter" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="itbv__head">Employee</th>
            <th scope="col" class="itbv__head">Status</th>
            <th scope="col" class="itbv__head">Learning Seen</th>
            <th scope="col" class="itbv__head itbv__head--center">Quiz</th>
            <th scope="col" class="itbv__head">Inspected By</th>
            <th scope="col" class="itbv__head">Inspection</th>
            <th scope="col" class="itbv__head" aria-label="Reset progress">Reset</th>
            <th scope="col" class="itbv__head" aria-label="Chatter"></th>
          </tr>
        </thead>

        <tbody
          v-for="group in displayGroups"
          :key="group.key"
          class="itbv__group"
        >
          <tr class="itbv__group-header-row">
            <th scope="colgroup" colspan="8" class="itbv__group-header">
              <span class="itbv__group-header-text">{{ group.headerText }}</span>
              <span v-if="group.configText" class="itbv__group-header-config">
                {{ group.configText }}
              </span>
            </th>
          </tr>
          <tr
            v-for="(row, index) in group.rows"
            :key="rowKey(row, index)"
            class="itbv__row"
            :class="{ 'itbv__row--reset-pending': !group.showResetAction }"
          >
            <td class="itbv__cell itbv__cell--employee">
              <div class="itbv__cell-inner">
                <EmployeeCard
                  v-if="row.employee"
                  class="itbv__employee-card"
                  v-bind="employeeProps(row.employee)"
                  :show-availability="false"
                  :show-importance-selector="false"
                  :is-hover-enabled="false"
                />
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--badge">
              <div class="itbv__cell-inner">
                <div class="itbv__stack">
                  <span
                    class="itbv__badge"
                    :class="`itbv__badge--${statusModifier(row.status)}`"
                  >
                    {{ row.status || "—" }}
                  </span>
                  <span v-if="formatOnDate(statusDateOf(row))" class="itbv__on-date">
                    {{ formatOnDate(statusDateOf(row)) }}
                  </span>
                </div>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--badge">
              <div class="itbv__cell-inner">
                <div v-if="row.learningSeenDate" class="itbv__stack">
                  <span class="itbv__badge itbv__badge--passed">Learning Seen</span>
                  <span class="itbv__on-date">{{ formatOnDate(row.learningSeenDate) }}</span>
                </div>
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--center itbv__cell--badge">
              <div class="itbv__cell-inner">
                <div v-if="row.quizStatus" class="itbv__stack">
                  <span
                    class="itbv__badge"
                    :class="`itbv__badge--${outcomeModifier(row.quizStatus)}`"
                  >
                    {{ quizLabel(row.quizStatus) }}
                  </span>
                  <span v-if="quizDateOf(row)" class="itbv__on-date">
                    {{ formatOnDate(quizDateOf(row)) }}
                  </span>
                </div>
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--employee">
              <div class="itbv__cell-inner">
                <EmployeeCard
                  v-if="row.inspectedBy"
                  class="itbv__employee-card"
                  v-bind="employeeProps(row.inspectedBy)"
                  :show-availability="false"
                  :show-importance-selector="false"
                  :is-hover-enabled="false"
                />
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--badge itbv__cell--inspection">
              <div class="itbv__cell-inner">
                <div v-if="row.inspectionStatus" class="itbv__stack">
                  <span
                    class="itbv__badge"
                    :class="`itbv__badge--${outcomeModifier(row.inspectionStatus)}`"
                  >
                    {{ inspectionLabel(row.inspectionStatus) }}
                  </span>
                  <span v-if="inspectionDateOf(row)" class="itbv__on-date">
                    {{ formatOnDate(inspectionDateOf(row)) }}
                  </span>
                </div>
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--action itbv__cell--reset">
              <div class="itbv__cell-inner">
                <button
                  v-if="group.showResetAction && row.canReset !== false"
                  type="button"
                  class="itbv__icon-button"
                  :aria-label="`Reset ${employeeName(row.employee)}'s progress for ${group.versionLabel}`"
                  @click="onReset(row, group.source)"
                >
                  <img
                    class="itbv__reset-icon"
                    :src="refreshIcon"
                    alt=""
                    width="24"
                    height="24"
                    aria-hidden="true"
                  />
                </button>
                <span v-else class="itbv__empty-value">—</span>
              </div>
            </td>

            <td class="itbv__cell itbv__cell--action itbv__cell--chatter">
              <div class="itbv__cell-inner">
                <button
                  type="button"
                  class="itbv__icon-button"
                  :aria-label="`Open chatter for ${employeeName(row.employee)}'s ${group.versionLabel} inspection`"
                  @click="onChatter(row, group.source)"
                >
                  <img
                    class="itbv__chatter-icon"
                    :src="chatterIcon"
                    alt=""
                    width="24"
                    height="24"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="!loading" class="itbv__empty">No inspections found.</div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import {
  fetchCheckpointEvaluationChatter,
  resolveAccountIdFromStorage,
  resolveCheckpointEvaluationRecordId,
} from "@components/utils/checkpointEvaluationChatter";
import refreshIcon from "./refresh-ccw-01.svg";
import chatterIcon from "./chatter.svg";

const props = defineProps({
  /**
   * One entry per checkpoint version, newest/current first.
   * Shape: `{ id?, versionLabel, publishedDate?, isCurrent?, rows[] }`.
   * `id` is used as the `versionId` payload for `reset` / `chatterOpen`.
   */
  versionGroups: {
    type: Array,
    default: () => [],
  },
  /**
   * Rows currently awaiting the current version (reset pending).
   * Rendered above the version groups when non-empty. Rows may carry their own
   * `versionId` (used for `reset` / `chatterOpen`) when the current version id is known.
   */
  resetGroup: {
    type: Array,
    default: () => [],
  },
  /**
   * Active filters passed through to the Filters bar above the table.
   * Shape: `{ [key]: label }` — one removable chip is rendered per truthy entry.
   * `filterChange` is emitted with this object (or an updated copy) whenever the
   * Filters bar changes.
   */
  filters: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Controlled search text for the "Search employee" input in the Filters bar.
   * `searchChange` is emitted with the new value whenever the user types.
   */
  employeeSearch: {
    type: String,
    default: "",
  },
  /** Shown over the table while filters or search refetch. */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the operator may bulk-reset every inspection (API `can_bulk_reset`).
   * Hides Reset All when false so unauthorized users never start that flow.
   */
  canBulkReset: {
    type: Boolean,
    default: true,
  },
  /** Disables Reset All while a bulk-reset request is in flight. */
  resetAllBusy: {
    type: Boolean,
    default: false,
  },
  /** Account id for checkpoint_evaluation chatter; falls back to `currentAccountID`. */
  accountId: {
    type: [String, Number],
    default: null,
  },
  /** Drawer title — typically the checkpoint criteria, same as coach inspect chatter. */
  chatterTypeValue: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "reset",
  "chatterOpen",
  "filterChange",
  "searchChange",
  "resetAll",
  "openFilters",
]);

const activeFilterChips = computed(() => {
  const filters = props.filters || {};
  return Object.entries(filters)
    .filter(([, label]) => label != null && String(label).trim() !== "")
    .map(([key, label]) => ({ key, label: String(label) }));
});

/** Labels in the same order as `activeFilterChips` — fed to SearchBar's `chip-text`. */
const chipLabels = computed(() => activeFilterChips.value.map((chip) => chip.label));

const hasAnyRows = computed(() => {
  if (props.resetGroup && props.resetGroup.length) return true;
  return (props.versionGroups || []).some(
    (group) => group && Array.isArray(group.rows) && group.rows.length > 0
  );
});

/**
 * Flattened group list for a single table body loop. Reset group is first (no reset
 * action — Figma shows an em-dash there); version groups follow with reset enabled.
 */
const displayGroups = computed(() => {
  const groups = [];
  if (props.resetGroup && props.resetGroup.length) {
    groups.push({
      key: "reset",
      headerText: "Reset",
      configText: "When started it will run on the current version at that time",
      rows: props.resetGroup,
      showResetAction: false,
      versionLabel: "the current version",
      source: null,
    });
  }
  (props.versionGroups || []).forEach((group, index) => {
    if (!group || !Array.isArray(group.rows) || !group.rows.length) return;
    groups.push({
      key: groupKey(group, index),
      headerText: groupHeaderText(group),
      configText: groupConfigurationSummary(group),
      rows: group.rows,
      showResetAction: true,
      versionLabel: group.versionLabel || "this version",
      source: group,
    });
  });
  return groups;
});

function formatDate(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Figma date under badges: "On Aug 19, 2026". */
function formatOnDate(value) {
  const formatted = formatDate(value);
  return formatted ? `On ${formatted}` : null;
}

function groupHeaderText(group) {
  const label = group.versionLabel || "Version";
  const currentSuffix = group.isCurrent ? " (current)" : "";
  const published = formatDate(group.publishedDate || group.publishedAt);
  return published
    ? `${label}${currentSuffix} · Published ${published}`
    : `${label}${currentSuffix}`;
}

function groupConfigurationSummary(group) {
  return (group && group.configurationSummary) || "";
}

function statusModifier(status) {
  switch (String(status || "").toLowerCase()) {
    case "in progress":
      return "in-progress";
    case "passed":
      return "passed";
    case "failed":
      return "failed";
    case "not applicable":
      return "not-applicable";
    case "not started":
    default:
      return "not-started";
  }
}

function outcomeModifier(status) {
  switch (String(status || "").toLowerCase()) {
    case "passed":
    case "quiz passed":
    case "inspection passed":
    case "learning seen":
      return "passed";
    case "failed":
    case "quiz failed":
    case "inspection failed":
      return "failed";
    case "not required":
    case "not attempted":
    default:
      return "not-required";
  }
}

/**
 * Prefer an explicit status date; otherwise fall back to inspection / quiz / learning
 * dates so existing mock rows still render the Figma "On …" line under Status.
 */
function statusDateOf(row) {
  if (!row) return null;
  return row.statusDate || row.inspectedOnDate || row.quizDate || row.learningSeenDate || null;
}

/** Figma never shows an "On …" line under Not Required badges. */
function isNotRequiredStatus(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return normalized === "not required" || normalized === "not_required";
}

function quizDateOf(row) {
  if (!row || isNotRequiredStatus(row.quizStatus)) return null;
  return row.quizDate ?? null;
}

function inspectionDateOf(row) {
  if (!row || isNotRequiredStatus(row.inspectionStatus)) return null;
  return row.inspectedOnDate || null;
}

function titleCaseStatus(status) {
  return String(status || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Figma quiz badges: "Quiz Passed" / "Quiz Failed"; keep "Not Required" as-is. */
function quizLabel(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (normalized === "passed" || normalized === "quiz passed") return "Quiz Passed";
  if (normalized === "failed" || normalized === "quiz failed") return "Quiz Failed";
  if (normalized === "not required" || normalized === "not_required") return "Not Required";
  if (normalized.startsWith("quiz ")) return titleCaseStatus(status);
  return titleCaseStatus(status);
}

/** Figma inspection badges: "Inspection Passed" / "Inspection Failed". */
function inspectionLabel(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (normalized === "passed" || normalized === "inspection passed") {
    return "Inspection Passed";
  }
  if (normalized === "failed" || normalized === "inspection failed") {
    return "Inspection Failed";
  }
  if (normalized === "not required" || normalized === "not_required") return "Not Required";
  if (normalized.startsWith("inspection ")) return titleCaseStatus(status);
  return titleCaseStatus(status);
}

/**
 * Maps an employee record onto EmployeeCard props. Department is included so cells
 * match the Figma EmployeeCard (name + title + org/department line).
 */
function employeeProps(employee) {
  if (!employee) return {};
  return {
    firstName: employee.firstName || employee.first_name || "",
    lastName: employee.lastName || employee.last_name || "",
    position: employee.position || employee.title || "",
    photoUrl: employee.photoUrl || employee.avatar || "",
    department: employee.department || employee.department_name || employee.organization || "",
  };
}

function employeeName(employee) {
  const mapped = employeeProps(employee);
  const fullName = `${mapped.firstName} ${mapped.lastName}`.trim();
  return fullName || "employee";
}

function employeeIdOf(row) {
  if (!row || !row.employee) return null;
  return row.employee.id != null ? row.employee.id : row.employee.employeeId ?? null;
}

function versionIdFor(row, group) {
  if (row.versionId != null) return row.versionId;
  return group && group.id != null ? group.id : null;
}

function rowKey(row, index) {
  const employeeId = employeeIdOf(row);
  return employeeId != null ? `emp-${employeeId}` : `row-${index}`;
}

function groupKey(group, index) {
  return group.id != null ? `group-${group.id}` : `group-${index}`;
}

function rowActionPayload(row, group) {
  const payload = {
    employeeId: employeeIdOf(row),
    versionId: versionIdFor(row, group),
  };
  if (row?.id != null) payload.id = row.id;
  return payload;
}

function onReset(row, group) {
  emit("reset", rowActionPayload(row, group));
}

const chatterDrawerOpen = ref(false);
const chatterList = ref([]);
const totalChatter = ref(0);
const chatterPage = ref(1);
const chatterLoading = ref(false);
const chatterListData = ref({});
const chatterDrawerTitle = ref("");

async function fetchChatter(page = 1, append = false) {
  const accountId = resolveAccountIdFromStorage(props.accountId);
  const recordId = resolveCheckpointEvaluationRecordId(chatterListData.value);
  if (accountId == null || recordId == null || chatterLoading.value) return;

  chatterLoading.value = true;
  try {
    const { results, count } = await fetchCheckpointEvaluationChatter(accountId, recordId, page);
    totalChatter.value = count;
    chatterList.value = append ? [...chatterList.value, ...results] : results;
    chatterPage.value = page;
  } catch (error) {
    console.error("Error loading checkpoint evaluation chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function onChatter(row, group) {
  emit("chatterOpen", rowActionPayload(row, group));
  if (chatterLoading.value) return;

  const recordId = resolveCheckpointEvaluationRecordId(row);
  if (recordId == null) return;

  chatterDrawerTitle.value = props.chatterTypeValue || employeeName(row.employee);
  chatterListData.value = {
    id: recordId,
    criteria: chatterDrawerTitle.value,
  };
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
  chatterDrawerOpen.value = true;
}

async function loadMoreChatter() {
  if (totalChatter.value <= chatterList.value.length) return;
  await fetchChatter(chatterPage.value + 1, true);
}

function onChatterDrawerClose(data) {
  if (!data) return;
  chatterDrawerOpen.value = false;
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
}

function emitOpenFilters() {
  emit("openFilters");
}

function onSearchText(text) {
  emit("searchChange", text);
}

function emitResetAll() {
  emit("resetAll");
}

/**
 * SearchBar chips only carry their display label (SearchBar emits `removeFilter` with
 * the removed chip text), so remove every active-filter key whose label matches —
 * equivalent to the old key-based removal when each dimension contributes one chip.
 */
function removeFilterByLabel(label) {
  const next = { ...(props.filters || {}) };
  Object.keys(next).forEach((key) => {
    if (next[key] != null && String(next[key]) === String(label)) delete next[key];
  });
  emit("filterChange", next);
}
</script>

<style scoped>
/* Tokens from Figma node 46887:280625 — no invented chrome. */
.itbv {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
}

.itbv__filters-bar {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 84px;
  padding: 20px 0;
  border: 0;
  background: transparent;
}

.itbv__search-bar {
  display: flex !important;
  flex: 1 1 auto;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.itbv__search-bar ::v-deep .col,
.itbv__search-bar ::v-deep .filter-button,
.itbv__search-bar ::v-deep .search-button {
  flex: 0 1 auto;
  width: auto;
  max-width: none;
  padding: 0 !important;
  margin: 0;
}

.itbv__search-bar ::v-deep .filter-button {
  flex: 1 1 auto;
}

.itbv__search-bar ::v-deep .search-button {
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: flex-end;
}

.itbv__search-bar ::v-deep .filter-input-search {
  width: 320px;
  max-width: 320px;
  height: 44px;
  margin: 0;
  float: none;
}

.itbv__search-bar ::v-deep .main_label:empty {
  display: none;
}

.itbv__search-bar ::v-deep .ex_text_input,
.itbv__search-bar ::v-deep .ex_text_input .input-search,
.itbv__search-bar ::v-deep .ex_text_input .v-input,
.itbv__search-bar ::v-deep .ex_text_input .v-input__control {
  height: 44px !important;
  min-height: 44px !important;
  margin: 0 !important;
}

.itbv__search-bar ::v-deep .ex_text_input .v-input__slot {
  height: 44px !important;
  min-height: 44px !important;
  margin: 0 !important;
}

.itbv__search-bar ::v-deep .ex_text_input .v-text-field__details {
  display: none !important;
}

.itbv__search-bar ::v-deep .ex_text_input fieldset {
  border-radius: 8px !important;
}

.itbv__reset-all-button {
  display: inline-flex;
  box-sizing: border-box;
  flex-shrink: 0;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  height: 44px;
  margin: 0;
  padding: 12px 18px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.itbv__reset-all-button:hover:not(:disabled) {
  background: #f9fafb;
}

.itbv__reset-all-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.itbv__icon-button:focus-visible,
.itbv__reset-all-button:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.itbv__body {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 120px;
}

.itbv__loader {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  background: rgba(255, 255, 255, 0.72);
}

.itbv__loader-spinner {
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  border: 3px solid #e4e7ec;
  border-top-color: #7f56d9;
  border-radius: 50%;
  animation: itbv-spin 0.7s linear infinite;
}

@keyframes itbv-spin {
  to {
    transform: rotate(360deg);
  }
}

.itbv__table-wrap {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  background: #ffffff;
}

/* Fluid row grid: badge columns keep a min that fits "Inspection Passed";
   employee columns grow so the table fills the panel. */
.itbv__table {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
}

.itbv__table colgroup {
  display: none;
}

.itbv__table thead,
.itbv__group {
  display: contents;
}

.itbv__table thead tr,
.itbv__group-header-row,
.itbv__row {
  display: grid;
  grid-template-columns:
    minmax(200px, 1.5fr)
    minmax(128px, 0.75fr)
    minmax(136px, 0.8fr)
    minmax(124px, 0.75fr)
    minmax(200px, 1.5fr)
    minmax(168px, 1fr)
    64px
    48px;
  width: 100%;
}

.itbv__col--employee {
  width: 200px;
}

.itbv__col--status {
  width: 128px;
}

.itbv__col--learning-seen {
  width: 136px;
}

.itbv__col--quiz {
  width: 124px;
}

.itbv__col--inspected-by {
  width: 200px;
}

.itbv__col--inspection {
  width: 168px;
}

.itbv__col--reset {
  width: 64px;
}

.itbv__col--chatter {
  width: 48px;
}

.itbv__head {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  background: #f9fafb;
  border: 0;
  color: #6b737d;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  text-align: left;
  white-space: nowrap;
}

.itbv__head--center {
  justify-content: center;
  text-align: center;
}

.itbv__group-header-row {
  background: transparent;
}

.itbv__group-header {
  display: flex;
  gap: 20px;
  align-items: center;
  grid-column: 1 / -1;
  height: 32px;
  padding: 0 12px;
  background: #f2f4f7;
  border: 0;
  color: #26305c;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  text-align: left;
}

.itbv__group-header-text {
  color: #26305c;
  font-weight: 600;
}

.itbv__group-header-config {
  color: #667085;
  font-size: 11px;
  font-weight: 400;
}

.itbv__cell {
  height: 65px;
  padding: 0;
  border-bottom: 0;
  vertical-align: top;
}

.itbv__cell-inner {
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  height: 65px;
  max-height: 65px;
  padding: 0 12px;
  overflow: hidden;
}

.itbv__cell--badge .itbv__cell-inner {
  padding: 0 8px;
  overflow: visible;
}

.itbv__cell--inspection .itbv__cell-inner {
  min-width: 0;
}

.itbv__cell--employee {
  min-width: 180px;
}

.itbv__cell--center .itbv__cell-inner {
  justify-content: center;
}

.itbv__cell--center .itbv__stack {
  align-items: center;
}

.itbv__cell--action .itbv__cell-inner {
  align-items: center;
  overflow: visible;
}

.itbv__cell--reset .itbv__cell-inner {
  justify-content: center;
}

.itbv__cell--chatter .itbv__cell-inner {
  justify-content: flex-start;
}

/* Reset group: Figma top-aligns the em-dash, empty reset, and chatter with the badges. */
.itbv__row--reset-pending .itbv__cell--action .itbv__cell-inner {
  align-items: flex-start;
}

.itbv__row--reset-pending .itbv__cell--reset .itbv__cell-inner {
  justify-content: flex-start;
}

.itbv__employee-card {
  align-items: flex-start !important;
  gap: 5px;
  max-width: 100%;
  margin: 0;
  padding: 0;
}

.itbv__employee-card ::v-deep .v-avatar {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
}

.itbv__employee-card ::v-deep .employee-card-avatar,
.itbv__employee-card ::v-deep .employee_with_availability {
  display: flex;
  align-items: flex-end;
  width: 52px;
  min-width: 52px;
  height: 40px;
  margin-right: 0 !important;
}

.itbv__employee-card.mr-5,
.itbv__employee-card ::v-deep .mr-5 {
  margin-right: 0 !important;
}

.itbv__employee-card ::v-deep .ml-5 {
  margin-left: 0 !important;
}

.itbv__employee-card ::v-deep .employee-info {
  padding-left: 0;
  margin-left: 0 !important;
}

.itbv__employee-card ::v-deep .text_name {
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.itbv__employee-card ::v-deep .text_position {
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.itbv__employee-card ::v-deep .text_department-container {
  gap: 4.15px;
  margin-top: 0;
}

.itbv__employee-card ::v-deep .text_department {
  color: #101828;
  font-size: 10.38px;
  font-weight: 500;
  line-height: 24.911px;
}

.itbv__stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.itbv__on-date {
  color: #4d5763;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.itbv__empty-value {
  color: #99a1ab;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.itbv__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #666666;
  cursor: pointer;
}

.itbv__icon-button:hover {
  opacity: 0.8;
}

.itbv__reset-icon,
.itbv__chatter-icon {
  display: block;
  width: 24px;
  height: 24px;
}

.itbv__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
}

.itbv__badge--not-started,
.itbv__badge--not-required,
.itbv__badge--not-applicable {
  background: #f3f4f6;
  border-color: #d3d5d9;
  color: #343c45;
}

.itbv__badge--in-progress {
  background: #fffaec;
  border-color: #fedf89;
  color: #b54708;
}

.itbv__badge--passed {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.itbv__badge--failed {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.itbv__empty {
  padding: 40px 16px;
  color: #667085;
  font-size: 14px;
  text-align: center;
}
</style>
